import { createRootRoute, createRoute, redirect } from '@tanstack/react-router'
import { AuthGuard } from '@/features/auth/AuthGuard'
import { AuthenticatedLayout } from '@/layouts/AuthenticatedLayout'
import { RootLayout } from './root'
import { LoginPage } from './login'
import { RegisterPage } from './register'
import { HouseholdSetupPage } from './household-setup'
import { HomePage } from './home'
import { TemplatesPage } from './templates'
import { HouseholdPage } from './household'
import { AccountPage } from './account'
import { PantryView } from '@/features/pantry/PantryView'
import { RecipesView } from '@/features/recipes/RecipesView'
import { RecipeDetail } from '@/features/recipes/RecipeDetail'
import { RecipeFormPage } from '@/features/recipes/RecipeFormPage'
import { RecipeEditPage } from '@/features/recipes/RecipeEditPage'
import { MealPlanView } from '@/features/meal-plan/MealPlanView'
import { GroceryListView } from '@/features/grocery-list/GroceryListView'
import { MorePage } from './more'
import { OnboardingPage } from './onboarding'

// Root route — uses RootLayout which mounts RouterFocusManager inside Router context
const rootRoute = createRootRoute({
  component: RootLayout,
})

// Index route — redirect / to /login
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => redirect({ to: '/login' }),
})

// Public routes — no bottom nav
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
})

// Auth guard — validates session before rendering children
const authGuardRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth-guard',
  component: AuthGuard,
})

// Household setup — authenticated but no bottom nav (user has no household yet)
const householdSetupRoute = createRoute({
  getParentRoute: () => authGuardRoute,
  path: '/household-setup',
  component: HouseholdSetupPage,
})

// Onboarding — authenticated but no bottom nav (new users only)
const onboardingRoute = createRoute({
  getParentRoute: () => authGuardRoute,
  path: '/onboarding',
  component: OnboardingPage,
})

// Authenticated layout — AuthGuard + BottomNav shell
const authenticatedLayoutRoute = createRoute({
  getParentRoute: () => authGuardRoute,
  id: 'authenticated',
  component: AuthenticatedLayout,
})

const homeRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/home',
  component: HomePage,
})

const templatesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/templates',
  component: TemplatesPage,
})

const householdRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/household',
  component: HouseholdPage,
})

const accountRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/account',
  component: AccountPage,
})

// Placeholder routes for Epic 2 features (to be implemented in stories 2.3–2.7)
const pantryRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/pantry',
  component: PantryView,
})

const planRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/plan',
  component: MealPlanView,
})

const recipesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/recipes',
  component: RecipesView,
})

const recipesNewRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/recipes/new',
  component: RecipeFormPage,
})

const recipeDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/recipes/$recipeId',
  component: RecipeDetail,
})

const recipeEditRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/recipes/$recipeId/edit',
  component: RecipeEditPage,
})

const moreRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/more',
  component: MorePage,
})

const groceryListRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/grocery-list',
  component: GroceryListView,
})

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  authGuardRoute.addChildren([
    householdSetupRoute,
    onboardingRoute,
    authenticatedLayoutRoute.addChildren([
      homeRoute,
      templatesRoute,
      householdRoute,
      accountRoute,
      pantryRoute,
      planRoute,
      recipesRoute,
      recipesNewRoute,
      recipeDetailRoute,
      recipeEditRoute,
      groceryListRoute,
      moreRoute,
    ]),
  ]),
])
